import {
  type SquawkPagesParams,
  squawkGroupPages,
  squawksInfiniteQuery,
} from "@common/api/quoting/squawk"
import type { SquawksFilterOptionsData } from "@common/client"
import type { QueryClient } from "@tanstack/react-query"
import { z } from "zod"
import { aircraftTypesSchema } from "@/routes/_layout/quotes_/$quoteId/-lib/aircraft-types"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  withFixed,
} from "./columns"
import { type Grouping, groupChoiceSchema, groupingSchema } from "./grouping"

export const TABS = ["all", "subscribed", "closed", "archived"] as const

const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

export const statusFilterSchema = z.enum([
  "pending_review",
  "approved",
  "rejected",
  "verified",
])

export const severityFilterSchema = z.enum([
  "airworthiness",
  "informational",
  "none",
])

const directionSchema = z.enum(["asc", "desc"])

const layoutSchema = z.enum(["list"])

export type Direction = z.infer<typeof directionSchema>

export const squawksSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  status: z.array(statusFilterSchema).optional().catch(undefined),
  severity: z.array(severityFilterSchema).optional().catch(undefined),
  work_order: z.array(z.string()).optional().catch(undefined),
  customer: z.array(z.string()).optional().catch(undefined),
  reporter: z.array(z.string()).optional().catch(undefined),
  ata: z.array(z.number()).optional().catch(undefined),
  layout: layoutSchema.optional().catch(undefined),
  group: groupChoiceSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
  aircraft_types: aircraftTypesSchema.optional().catch(undefined),
})

export type SquawksSearch = z.infer<typeof squawksSearchSchema>

export type SquawksDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Column
  direction: Direction
}

export type SquawkOptionsParams = NonNullable<SquawksFilterOptionsData["query"]>

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "all":
      return "All"
    case "subscribed":
      return "Subscribed"
    case "closed":
      return "Closed"
    case "archived":
      return "Archived"
  }
}

export function squawkOptionsParams(
  search: Pick<SquawksSearch, "tab" | "search">,
): SquawkOptionsParams {
  const tab = search.tab ?? "all"
  return {
    search: search.search || null,
    archived: tab === "archived",
    scope: tab === "subscribed" || tab === "closed" ? tab : "all",
  }
}

export function squawkPagesParams(search: SquawksSearch): SquawkPagesParams {
  const display = displayOf(search)
  return {
    ...squawkFilters(search),
    sort: display.sort,
    direction: display.direction,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}

function squawkFilters(search: SquawksSearch): SquawkPagesParams {
  return {
    ...squawkOptionsParams(search),
    status: search.status?.length ? search.status : null,
    work_package_number: search.work_order?.length ? search.work_order : null,
    customer_id: search.customer?.length ? search.customer : null,
    reported_by_id: search.reporter?.length ? search.reporter : null,
    severity: search.severity?.length ? search.severity : null,
    ata_chapter: search.ata?.length ? search.ata : null,
  }
}

export async function ensureSquawks(
  queryClient: QueryClient,
  params: SquawkPagesParams,
  grouped: boolean,
): Promise<void> {
  const listed = squawksInfiniteQuery(params)
  if (grouped) {
    void queryClient.prefetchInfiniteQuery(listed)
    await queryClient.ensureQueryData(squawkGroupPages.groups(params))
  } else {
    await queryClient.ensureInfiniteQueryData(listed)
  }
}

export function displayOf(search: SquawksSearch): SquawksDisplay {
  const group =
    search.layout === "list" || search.group === "none"
      ? undefined
      : (search.group ?? "work_order")
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    columns: search.columns ? withFixed(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "reported",
    direction: search.direction ?? (search.sort ? "asc" : "desc"),
  }
}

export function columnsSearch(columns: Column[]): Partial<SquawksSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

export function sortSearch(
  display: SquawksDisplay,
  column: Column,
): Partial<SquawksSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "reported" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}
